import { Injectable } from '@angular/core'
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs'

/** What the currently-showing tour step needs the app's chrome to do (CNCT-6771). */
export interface ITourStepFocus {
  /** `data-tour-id` of the nav group to hold expanded; `null` collapses every group (e.g. Messages, which has none). */
  nav: string | null
  /** Whether the header account dropdown must be held open (the account-menu steps). */
  accountMenu: boolean
}

/**
 * The tour's focus bus (CNCT-6771).
 *
 * design-contract.md → §Surface 3.2 (indicator placement is anchored; the tour may open/expand the
 * chrome its subject lives in).
 *
 * `GuideService` publishes what the current step needs; the surfaces that own that state subscribe —
 * `NavMenuComponent` collapses every nav group but the focused one, `ProfileDropdownComponent` holds
 * the account menu open. Deliberately dependency-free: it is imported by app-level chrome components
 * that the guide's own collaborators already depend on, and a bus with zero injections cannot close a
 * cycle. It also keeps the guide from reaching into the DOM of surfaces it does not own.
 *
 * `null` means "no tour step is driving the chrome" — the surfaces restore whatever the user had.
 */
@Injectable({ providedIn: 'root' })
export class TourFocusService {
  private readonly focusSubject = new BehaviorSubject<ITourStepFocus | null>(null)

  /** What the current step needs, or `null` when no tour step is showing. */
  readonly focus$ = this.focusSubject.asObservable()

  /** The nav group to hold expanded (`null` = collapse them all, or no tour running). */
  readonly navFocus$: Observable<string | null> = this.focus$.pipe(
    map(focus => focus?.nav ?? null),
    distinctUntilChanged()
  )

  /** Whether the account dropdown should be held open. */
  readonly accountMenuOpen$: Observable<boolean> = this.focus$.pipe(
    map(focus => !!focus?.accountMenu),
    distinctUntilChanged()
  )

  /** Synchronous read of "is a tour step currently driving the chrome" (route-driven nav expansion defers to it). */
  get engaged(): boolean {
    return this.focusSubject.value !== null
  }

  /** Publish the current step's needs. Called on every step, so a previous step's focus never lingers. */
  setStepFocus(focus: ITourStepFocus): void {
    this.focusSubject.next(focus)
  }

  /** Hand the chrome back to the user — every subscriber restores its pre-tour state. Idempotent. */
  clear(): void {
    if (this.focusSubject.value !== null) this.focusSubject.next(null)
  }
}
